/**
 * Frontend Error Reporter — sends errors to backend /api/v1/error-report
 * + (SEG-PG-6.1, 2026-05-21) ALSO calls @sentry/nextjs SDK when DSN configured.
 *
 * Captures:
 * - React ErrorBoundary crashes
 * - Unhandled promise rejections
 * - API call failures (4xx/5xx)
 * - Web Vitals (LCP, FID, CLS, FCP, TTFB)
 *
 * Dual-path design (互补 not 替代):
 * - Custom POST → /api/v1/error-report → backend Sentry + audit_logs
 *   (server-side aggregation + HMAC audit trail + Lark alert)
 * - Sentry SDK direct (SEG-PG-6.1, gated on NEXT_PUBLIC_SENTRY_DSN)
 *   → source maps + breadcrumbs + performance traces + replay (client-side richness)
 */

import {
  captureExceptionGraceful,
  captureMessageGraceful,
} from "./sentry-bridge";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";
const REPORT_URL = `${API_BASE}/api/v1/error-report`;

// Deduplication: don't report the same error twice in 60s
const _reported = new Map<string, number>();
const DEDUPE_WINDOW_MS = 60_000;

function _isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = _reported.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  _reported.set(key, now);
  // Evict old entries
  if (_reported.size > 200) {
    for (const [k, v] of _reported) {
      if (now - v > DEDUPE_WINDOW_MS) _reported.delete(k);
    }
  }
  return false;
}

async function _send(payload: Record<string, unknown>): Promise<void> {
  try {
    await fetch(REPORT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true, // Ensure delivery even on page unload
    });
  } catch {
    // Silent — monitoring should never break the app
  }
}

/** Report a React ErrorBoundary crash */
export function reportErrorBoundary(
  error: Error,
  componentStack?: string,
): void {
  const key = `eb:${error.message}`;
  if (_isDuplicate(key)) return;

  _send({
    type: "error_boundary",
    message: error.message,
    stack: (error.stack || "") + "\n\nComponent Stack:\n" + (componentStack || ""),
    url: typeof window !== "undefined" ? window.location.href : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    component: componentStack?.split("\n")[1]?.trim() || "",
  });

  // SEG-PG-6.1: ALSO forward to native Sentry SDK (source map decoded stack)
  captureExceptionGraceful(error, {
    type: "error_boundary",
    componentStack: componentStack || "",
  });
}

/** Report an unhandled promise rejection */
export function reportUnhandledRejection(reason: unknown): void {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : "Unknown rejection";
  const key = `ur:${message}`;
  if (_isDuplicate(key)) return;

  _send({
    type: "unhandled_rejection",
    message,
    stack: reason instanceof Error ? reason.stack || "" : "",
    url: typeof window !== "undefined" ? window.location.href : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  });

  // SEG-PG-6.1: ALSO forward to native Sentry SDK
  captureExceptionGraceful(reason instanceof Error ? reason : new Error(message), {
    type: "unhandled_rejection",
  });
}

/** Report an API call failure */
export function reportApiError(
  path: string,
  status: number,
  errorText: string,
): void {
  // Only report 5xx errors (client errors are expected)
  if (status < 500) return;

  const key = `api:${path}:${status}`;
  if (_isDuplicate(key)) return;

  _send({
    type: "api_error",
    message: `${status} on ${path}: ${errorText.slice(0, 200)}`,
    url: typeof window !== "undefined" ? window.location.href : "",
    component: path,
  });

  // SEG-PG-6.1: ALSO forward to native Sentry SDK (as message, not exception)
  captureMessageGraceful(
    `API ${status} on ${path}`,
    "error",
    { path, status, errorText: errorText.slice(0, 200) },
  );
}

/** Report a Web Vital metric */
export function reportWebVital(
  name: string,
  value: number,
  rating: "good" | "needs-improvement" | "poor",
): void {
  // Only report poor metrics to avoid noise
  if (rating !== "poor") return;

  _send({
    type: "web_vitals",
    message: `${name}: ${value.toFixed(1)}`,
    metric_name: name,
    metric_value: value,
    metric_rating: rating,
    url: typeof window !== "undefined" ? window.location.href : "",
  });
}

/** Initialize global error listeners (call once on app mount) */
export function initGlobalErrorListeners(): void {
  if (typeof window === "undefined") return;

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    reportUnhandledRejection(event.reason);
  });

  // Uncaught errors
  window.addEventListener("error", (event) => {
    if (event.error) {
      const key = `ue:${event.error.message}`;
      if (_isDuplicate(key)) return;
      _send({
        type: "uncaught_error",
        message: event.error.message,
        stack: event.error.stack || "",
        url: event.filename || window.location.href,
        component: `${event.filename}:${event.lineno}:${event.colno}`,
      });
    }
  });
}
